import React, {useCallback, useEffect} from 'react'
import {NavigatorForMemberPage} from '../../../Navigator/NavigatorForMemberPage'
import MemberRepo from '../../../Repo/MemberRepo'
import {useParams} from 'react-router-dom'
import {CommonResponseStatus} from '../../../../Network/CommonResponse'
import {MBMemberError} from '../../../Common/MBMemberError'
import {useMBMemberError} from '../../Hooks/UseMBMemberError'
import MemberAgreementRepo from '../../../Repo/MemberAgreementRepo'
import {ConvenienceStoreInfo, CreditCardInfo, MobileCarrierKey} from '../../../Props/ContractInputInfo'
import {UrlQueryParams, useQuery} from '../../Hooks/UseQuery'

type MembersJoinResultPageProps = {
  navigator: NavigatorForMemberPage
  repo: MemberRepo
  memberAgreementRepo: MemberAgreementRepo
}
export const MembersJoinResultPage: React.VFC<MembersJoinResultPageProps> = (props) => {
  const {temporaryEnrollmentId} = useParams<{ temporaryEnrollmentId: string }>()

  const status = useQuery('status')
  if (!status) {
    throw new MBMemberError('SystemError')
  }

  const throwMBMemberError = useMBMemberError()

  const goToJoinCompletePage = useCallback(async () => {
    try {
      const resultGetBelongToBucketComplete =
        await props.repo.getBelongToBucketComplete(temporaryEnrollmentId)

      switch (resultGetBelongToBucketComplete.status) {
        case CommonResponseStatus.Ok:
          break
        case CommonResponseStatus.NotFound:
          // noinspection ExceptionCaughtLocallyJS
          throw new MBMemberError('NotFound')
        case CommonResponseStatus.InternalServerError:
          // noinspection ExceptionCaughtLocallyJS
          throw new MBMemberError('ServerError')
        default:
          // noinspection ExceptionCaughtLocallyJS
          throw new MBMemberError('UnknownError')
      }

      if (!resultGetBelongToBucketComplete.data) {
        // noinspection ExceptionCaughtLocallyJS
        throw new MBMemberError('SystemError')
      }
      props.navigator.goToJoinCompletePage(
        {
          mail: resultGetBelongToBucketComplete.data.memberInfo.mail,
          familyName: resultGetBelongToBucketComplete.data.memberInfo.familyName,
          firstName: resultGetBelongToBucketComplete.data.memberInfo.firstName,
          familyNameKana: '', firstNameKana: '',
          gender: null,
          birthday: '',
          postCode: '',
          addressPrefectureCode: '', addressCity: '', addressStreet: '', addressBuilding: '',
          phoneMobile: '', phoneTel: '',
          agreedIdList: [],
          mailMagazineIdCheckedList: [],
          enrollmentReasonSelectedInfo: undefined,
        },
        resultGetBelongToBucketComplete.data.signboard,
        resultGetBelongToBucketComplete.data.bucketMemberIdList,
        resultGetBelongToBucketComplete.data.contractInfo,
      )
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : 'SystemError'
      throwMBMemberError(errorMessage)
    }
  }, [props.repo, temporaryEnrollmentId])

  const goToJoinInputPage = useCallback(async () => {
    try {
      const resultGetJoinPreRegisterInfo =
        await props.repo.getJoinPreRegisterInfo(temporaryEnrollmentId)

      switch (resultGetJoinPreRegisterInfo.status) {
        case CommonResponseStatus.Ok:
          break
        case CommonResponseStatus.NotFound:
          // noinspection ExceptionCaughtLocallyJS
          throw new MBMemberError('NotFound')
        case CommonResponseStatus.InternalServerError:
          // noinspection ExceptionCaughtLocallyJS
          throw new MBMemberError('ServerError')
        default:
          // noinspection ExceptionCaughtLocallyJS
          throw new MBMemberError('SystemError')
      }

      if (!resultGetJoinPreRegisterInfo || !resultGetJoinPreRegisterInfo.data) {
        // noinspection ExceptionCaughtLocallyJS
        throw new MBMemberError('SystemError')
      }

      const signupKey = resultGetJoinPreRegisterInfo.data.signupKey

      const resultGetAgreementList =
        await props.memberAgreementRepo.get(signupKey)

      switch (resultGetAgreementList.status) {
        case CommonResponseStatus.Ok:
          break
        case CommonResponseStatus.NotFound:
          // noinspection ExceptionCaughtLocallyJS
          throw new MBMemberError('NotFound')
        case CommonResponseStatus.InternalServerError:
          // noinspection ExceptionCaughtLocallyJS
          throw new MBMemberError('ServerError')
        default:
          // noinspection ExceptionCaughtLocallyJS
          throw new MBMemberError('SystemError')
      }

      if (!resultGetAgreementList || !resultGetAgreementList.data) {
        // noinspection ExceptionCaughtLocallyJS
        throw new MBMemberError('SystemError')
      }

      const agreementList = resultGetAgreementList.data.map((agreement) => agreement.id)
      const mailMagazineIdCheckedList = resultGetJoinPreRegisterInfo.data.mailMagazineIdCheckedList
      const contractInfo = resultGetJoinPreRegisterInfo.data.contractInfo
      const enrollmentReason = resultGetJoinPreRegisterInfo.data.enrollmentReason

      props.navigator.backToMembersJoinInputPageCauseMobileCarrierError(
        signupKey,
        {
          term: contractInfo.contractTerm,
          method: contractInfo.paymentMethod,
          amount: 0,
          creditCard: {
            creditCardType: 'newCard',
            newCreditCardInfo: {
              cardNumber: '',
              expireMonth: '',
              expireYear: '',
              holderName: '',
              securityCode: '',
            },
            myCreditCardInfo: {
              selectedCardId: '',
              securityCodeMap: [],
            },
          } as CreditCardInfo,
          convenienceStore: {convenienceStoreKey: ''} as ConvenienceStoreInfo,
          mobileCarrier: {carrierKey: contractInfo.paymentMethodDetail as MobileCarrierKey},
        },
        agreementList,
        mailMagazineIdCheckedList,
        enrollmentReason,
      )
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : 'SystemError'
      throwMBMemberError(errorMessage)
    }
  }, [props.repo, props.memberAgreementRepo, temporaryEnrollmentId])

  useEffect(() => {
    switch (status) {
      case UrlQueryParams.status.ok:
        goToJoinCompletePage()
        break
      case UrlQueryParams.status.canceled:
        goToJoinInputPage()
        break
      default:
        throw new MBMemberError('SystemError')
    }
  }, [status])

  return null
}
